import { IpexMomentEnum } from '@insights/insights-data-provider';
import analytics, { reporter } from 'analytics-api';
import standardInsightsReporters from 'analytics-api/insights/standardInsightsReporters';
import { ReportCurrentPageEnum } from 'components/ProductSidebar/HomeDelivery/HomeDelivery';
import { InteractionParams } from './interaction';

const { giveMe, whatIsMe, makeItMe, helpMe, showMe, nonInteraction } = IpexMomentEnum;

export type ReportArticle = {
  articleNumber: string; // Global article number
  entityId: number;
};

export enum ReportSourceEnum {
  hamburgerMenu = 'hamburger_menu',
  summaryPageAddToCartSheet = 'summary_page_add_to_cart_sheet',
  summaryPageSaveDesignSheet = 'summary_page_save_design_sheet',
  openDesignSheet = 'open_design_sheet',
  saveDesignSheet = 'save_design_sheet',
  shareDesignSheet = 'share_design_sheet',
  headerLoginButton = 'header_login_button'
}

export const {
    reportAddToCart,
    reportAddToWishlist,
    reportInitPlanningSession,
    reportSaveDesign,
    reportVpcInteraction
  } = standardInsightsReporters(analytics),
  reportChangeArticle = reporter<
    InteractionParams & {
      placedArticles: ReportArticle[];
      removedArticles: ReportArticle[];
      targets: string[];
    }
  >('change_article', makeItMe),
  reportAddArticle = reporter<
    InteractionParams & {
      articles: ReportArticle[];
      targets: string[];
    }
  >('add_article', makeItMe),
  reportDeleteArticle = reporter<
    InteractionParams & {
      articles: ReportArticle[];
    }
  >('delete_article', makeItMe),
  reportNavigateMenu = reporter<InteractionParams>('navigate_menu', showMe),
  reportInteractWithMenu = reporter<InteractionParams>('interact_with_menu', showMe),
  reportBookRemotePlanning = reporter<{ currentMenu: string }>('book_remote_planning', helpMe),
  reportSelectTab = reporter<{ tabName: string }>('select_tab', showMe),
  reportAddMultiFrameInteriorCombination = reporter<{
    combinations: { assemblyId: string; articles: string[]; menu: string }[];
  }>('add_multiframe_interior_combination', makeItMe),
  reportCopyArticle = reporter<{ articleNumbers: string[] }>('copy_article', makeItMe),
  reportToggleRoomSize = reporter('toggle_room_size', makeItMe),
  reportCreateNew = reporter<{ currentPage: string }>('create_new', whatIsMe),
  reportPrint = reporter<{
    currentPage: string;
    your_design: boolean;
    product_list: boolean;
    assembly_overview: boolean;
    vpc: string;
  }>('print', giveMe),
  reportUndo = reporter('undo', makeItMe),
  reportRedo = reporter('redo', makeItMe),
  reportOpenFilter = reporter<{ menu: string }>('open_filter', showMe),
  reportToggleSorting = reporter<{ groupId: string; open: boolean }>('toggle_sorting', showMe),
  reportSortingApplied = reporter<{ groupId: string; subMenuId?: string; sortingId: string }>(
    'sorting_applied',
    showMe
  ),
  reportCloseFilter = reporter<{ menu: string }>('close_filter', showMe),
  reportShareDesignCardStateChange = reporter<{ state: string; source: ReportSourceEnum }>(
    'click_share_design_card_state',
    showMe
  ),
  reportCopyDesignCode = reporter('copy_design_code', giveMe),
  reportShareViaEmail = reporter('share_via_email', giveMe),
  reportShareViaSms = reporter('share_via_sms', giveMe),
  reportShareViaClipboard = reporter('share_via_clipboard', giveMe),
  reportToggleModal = reporter<{
    modal: string;
    visible: boolean;
    page: ReportCurrentPageEnum;
  }>('toggle_modal', showMe),
  reportSelectImage = reporter<{
    index: number;
    page: string;
  }>('select_image', showMe),
  reportToggleHamburgerMenu = reporter<{
    open: boolean;
  }>('toggle_hamburger_menu', showMe),
  reportLoadingScreen = reporter('loading_screen', nonInteraction),
  reportDesignErrorScreen = reporter<{ vpcCode?: string; sprId?: string; designId?: string }>(
    'load_design_error_screen',
    nonInteraction
  ),
  reportRemoveAllUnavailableProducts = reporter('remove_all_unavailable_articles', makeItMe),
  reportCreateNewUnavailableFrames = reporter('unavailable_frames_create_new', whatIsMe),
  reportReplaceUnavailableFrames = reporter<{ change_frame: string }>(
    'unavailable_frames_replace',
    makeItMe
  ),
  reportOpenQrCode = reporter<{ designCode: string }>('open_qr_code', nonInteraction),
  reportRenderPrintQrCode = reporter<{ designCode: string }>(
    'render_print_qr_code',
    nonInteraction
  ),
  reportHowToBuy = reporter('how_to_buy', giveMe),
  reportMoveArticle = reporter<{
    articleNumber: string;
    slantedWallOutOfBounds?: boolean | undefined;
  }>('move_article', makeItMe),
  reportChangeCornerSolutionOrientation = reporter('change_corner_frame_orientation', makeItMe),
  reportOpenProductInformation = reporter<InteractionParams & { articleNumber: string }>(
    'open_product_information',
    showMe
  ),
  reportShowSelectedMenuAdditionalItems = reporter<{ menu: string }>(
    'show_selected_menu_additional_items',
    showMe
  ),
  reportOnSummaryClicked = reporter<{ timeStamp: Date }>('on_summary_clicked', nonInteraction),
  reportSetPostcode = reporter<{
    page: ReportCurrentPageEnum;
  }>('set_postcode', showMe),
  reportSetStoreLocation = reporter<{
    page: ReportCurrentPageEnum;
    storeId: string;
  }>('set_store_location', showMe),
  reportDesignYouOwnSolutionClicked = reporter('design_your_own', whatIsMe),
  reportCurrentLocation = reporter<{ store_id: string | null; post_code: string | null }>(
    'current_location',
    nonInteraction
  ),
  reportHandlePromptAddHandles = reporter('handle_prompt_add_handles', whatIsMe),
  reportHandlePromptContinueWithout = reporter('handle_prompt_continue_without', whatIsMe),
  reportSetNoAvailability = reporter<{ exitButton: 'no_thanks_button' | 'x_button' }>(
    'set_no_availability',
    whatIsMe
  ),
  reportExperienceSurveyAccept = reporter<{ satisfaction_score: number; route: string }>(
    'experience_survey_accept',
    whatIsMe
  ),
  reportExperienceSurveyDecline = reporter<{ route: string }>(
    'experience_survey_decline',
    whatIsMe
  ),
  reportSurveyCoworkerHelpAccept = reporter<{ got_help: boolean; route: string }>(
    'survey_coworker_help_accept',
    whatIsMe
  ),
  reportSurveyCoworkerHelpDecline = reporter<{ route: string }>(
    'survey_coworker_help_decline',
    whatIsMe
  ),
  reportNavigation = reporter<{
    to: string;
    from: string;
    designCode?: string;
    userLeftFromBackButton: boolean;
    pageLoadTime: number;
  }>('navigation', whatIsMe),
  reportSelectBlueDot = reporter('select_blue_dot', showMe),
  reportSelectOpeningFunction = reporter<{ tab: string }>('select_opening_function', makeItMe),
  reportChangeSolutionFronts = reporter<{ color: string }>('change_solution_fronts', makeItMe),
  reportSelectReadyLayout = reporter<{ layout: string }>('select_ready_layout', makeItMe),
  reportLandingPageSelectStart = reporter<{
    start: 'resume_from_last' | 'guide_me' | 'design_from_scratch' | 'browse_inspiration';
  }>('landing_page_select_start', whatIsMe),
  reportProppingStatus = reporter<{ enable: boolean }>('toggle_propping', showMe),
  reportSelectEntity = reporter<{ articleNumber: string }>('select_item_in_scene', showMe),
  reportFrontsOpeningFunctionSheetChoice = reporter<{ choice: string }>(
    'fronts_opening_function_choice',
    showMe
  ),
  reportChangePushOpenFunction = reporter<{
    pushOpenSelected: boolean;
    allFronts: boolean;
    articles?: string[];
    frontType?: 'door' | 'drawer';
    menu: string;
  }>('change_push_open_function', makeItMe),
  reportDoorPromptReply = reporter<{
    userChoice: 'keep' | 'replace';
    promptType: 'open_shelves' | 'solid_doors' | 'glass_doors';
  }>('keep_door_prompt_reply', showMe),
  reportSlantedWallSpaceExceededSheet = reporter('prompt_slanted_wall_space_exceeded', showMe),
  reportSlantedWallShapeSizeChange = reporter<{
    dimensions?: number[][] | undefined;
    shape: string;
  }>('change_slanted_wall_shape_size', makeItMe),
  reportRoomSizeChange = reporter<{ dimension: string }>('change_room_size', makeItMe),
  reportSlantedWallShapeSelected = reporter<{
    dimensions?: number[][] | undefined;
    shape: string;
    menu: string;
  }>('select_slanted_wall_shape', makeItMe),
  reportTVStatus = reporter<{ enable: boolean }>('toggle_tv', showMe),
  reportMoveTv = reporter('move_tv', makeItMe),
  reportChangeTv = reporter<{
    newTvSize: number;
    oldTvSize: number | undefined;
    tvPlacement: 'wall' | 'foot';
  }>('change_tv', makeItMe),
  reportAutoFillLegsToggled = reporter<{
    enable: boolean;
  }>('toggle_autofill_high_legs', makeItMe),
  reportAutoFillFrameDepthChange = reporter<{
    depth: number;
  }>('change_autofill_frame_depth', makeItMe),
  reportAutoFillRecommendationChoice = reporter<{
    autoFillRecommendation: any;
    highLegs: boolean;
    solutionDepth: number;
  }>('choice_autofill_recommendation', makeItMe),
  reportTopsToggled = reporter<{ enable: boolean }>('toggle_tops', makeItMe),
  reportAddCustomSlidingDoor = reporter('add_custom_sliding_door', makeItMe),
  reportMemoryExceedsThreshold = reporter<{ heapSizeLimit: number; totalHeapSize: number }>(
    'memory_exceeds_threshold',
    nonInteraction
  ),
  reportRotate = reporter('rotate_frame', makeItMe),
  reportAccountLogin = reporter<{ source?: ReportSourceEnum }>('login_to_account', whatIsMe),
  reportAccountLogout = reporter<{ source?: ReportSourceEnum }>('logout_to_account', whatIsMe),
  reportSaveDesignClick = reporter<{ source?: ReportSourceEnum }>('click_save_design', whatIsMe),
  reportFinancialServicesMoreInfoClick = reporter<{ page: ReportCurrentPageEnum }>(
    'financial_services_more_info_click',
    whatIsMe
  ),
  reportFinancialServicesContinueButtonClick = reporter<{ page: ReportCurrentPageEnum }>(
    'financial_services_continue_button_click',
    whatIsMe
  ),
  reportSummaryLoaded = reporter('summary_loaded', whatIsMe),
  reportUserLoggedIn = reporter<{ loggedIn: boolean }>('user_logged_in', whatIsMe),
  reportAssistanceCardLinkClick = reporter('click_assistance_card_link', showMe);
